import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_LOCALE,
  MAT_DATE_FORMATS,
  MAT_NATIVE_DATE_FORMATS,
  NativeDateModule,
} from '@angular/material/core';

import { IonicModule } from '@ionic/angular';
import { TranslocoModule } from '@jsverse/transloco';
import { FORMLY_CONFIG, FormlyForm, provideFormlyCore } from '@ngx-formly/core';
import { FormlyIonicModule } from '@ngx-formly/ionic';
import { FormlyFormFieldModule } from '@ngx-formly/ionic/form-field';
import { withFormlyMaterial } from '@ngx-formly/material';
import { withFormlyFieldDatepicker } from '@ngx-formly/material/datepicker';
import { withFormlyFieldToggle } from '@ngx-formly/material/toggle';
import { FormFieldTabsUiComponent } from '@ts/shared/form/field/ui-tabs';
import { SharedFormUiFormScaffoldModule } from '@ts/shared/form/ui-form-scaffold';
import { SharedFormUiSubmitButtonModule } from '@ts/shared/form/ui-submit-button';
import { registerTranslateExtension } from '@ts/shared/form/util-i18n';
import { SharedFormWrapperUiIonFormFieldModule } from '@ts/shared/form/wrapper/ui-ion-form-field';
import { I18nService } from '@ts/shared/i18n/util-core';

import { InputFieldComponent } from './field/input/input-field.component';
import { PasswordFieldComponent } from './field/password/password.field.component';
import { SliderFieldComponent } from './field/slider/slider-field.component';
import { TextAreaFieldComponent } from './field/textarea/textarea-field.component';
import { showFormErrorMessage } from './form-error-message';
import { FormlyUiComponent } from './formly-ui/formly-ui.component';
import { LocaleDateAdapter } from './locale-date-adapter';
import { phoneNumberValidator } from './phone-number.validator';

@NgModule({
  imports: [
    CommonModule,
    FormlyFormFieldModule,
    FormlyIonicModule,
    FormlyForm,
    IonicModule,
    NativeDateModule,
    ReactiveFormsModule,
    SharedFormUiFormScaffoldModule,
    SharedFormUiSubmitButtonModule,
    SharedFormWrapperUiIonFormFieldModule,
    TranslocoModule,
  ],
  declarations: [FormlyUiComponent],
  exports: [FormlyUiComponent],
  providers: [
    {
      provide: FORMLY_CONFIG,
      multi: true,
      useFactory: registerTranslateExtension,
      deps: [I18nService],
    },
    { provide: DateAdapter, useClass: LocaleDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'id-ID' },
    provideFormlyCore([
      ...withFormlyMaterial(),
      withFormlyFieldDatepicker(),
      withFormlyFieldToggle(),
      {
        types: [
          {
            name: 'tabs',
            component: FormFieldTabsUiComponent,
          },
          {
            name: 'input',
            component: InputFieldComponent,
            wrappers: ['form-field-labeled'],
          },
          {
            name: 'password',
            component: PasswordFieldComponent,
          },
          {
            name: 'number',
            extends: 'input',
            defaultOptions: {
              props: {
                type: 'number',
              },
            },
          },
          {
            name: 'phone-number',
            extends: 'input',
            defaultOptions: {
              props: {
                type: 'tel',
                placeholder: '081xxxxxxxx',
              },
              validators: {
                validation: ['phone-number'],
              },
            },
          },
          {
            name: 'email',
            extends: 'input',
            defaultOptions: {
              props: {
                type: 'email',
                placeholder: 'your@email.com',
              },
              validators: {
                validation: ['email'],
              },
            },
          },
          {
            name: 'slider',
            component: SliderFieldComponent,
            wrappers: ['form-field-labeled'],
          },
          {
            name: 'textarea',
            component: TextAreaFieldComponent,
            wrappers: ['form-field-labeled'],
          },
          {
            name: 'file',
            extends: 'input',
            defaultOptions: {
              props: {
                type: 'file',
              },
            },
          },
        ],
        validators: [
          { name: 'email', validation: Validators.email },
          { name: 'phone-number', validation: phoneNumberValidator },
        ],
        validationMessages: [{ name: 'other', message: showFormErrorMessage }],
      },
    ]),
  ],
})
export class SharedFormUiFormlyModule {
  static forRoot(): ModuleWithProviders<SharedFormUiFormlyModule> {
    return {
      ngModule: SharedFormUiFormlyModule,
      providers: [
        {
          provide: FORMLY_CONFIG,
          multi: true,
          useFactory: registerTranslateExtension,
          deps: [I18nService],
        },
      ],
    };
  }
}
